<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        :style="{ zIndex: props.zIndex }"
        class="fixed inset-0 overflow-y-auto"
        @click="handleBackdropClick"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="contentId"
      >
        <!-- Backdrop -->
        <div :style="{ zIndex: props.zIndex - 1 }" class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        
        <!-- Modal container -->
        <div :style="{ zIndex: props.zIndex }" class="relative flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="opacity-100 translate-y-0 sm:scale-100"
            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              v-if="isOpen"
              ref="modalRef"
              :class="modalClasses"
              :style="modalStyles"
              @click.stop
            >
              <!-- Header -->
              <div v-if="title || $slots.header" class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <Heading v-if="title" :level="titleLevel" :id="titleId">
                  {{ title }}
                </Heading>
                <slot name="header" />
                
                <button
                  v-if="closable"
                  @click="close"
                  class="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none rounded-md p-1"
                  :aria-label="t('modal.closeModal')"
                >
                  <span class="sr-only">{{ t('modal.close') }}</span>
                  <Icon name="close" size="lg" />
                </button>
              </div>
              
              <!-- Body -->
              <div v-if="$slots.body" class="p-6" :id="contentId">
                <slot name="body" />
              </div>
              <div v-else class="p-6" :id="contentId">
                <slot />
              </div>
              
              <!-- Footer -->
              <div v-if="$slots.footer" class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <slot name="footer" />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Heading from './Heading.vue'
import Icon from './Icon.vue'
import { t } from '@/i18n/en-us'

// Generate unique IDs for accessibility
const generateId = () => `modal-${Math.random().toString(36).substr(2, 9)}`
const titleId = ref(generateId())
const contentId = ref(generateId())
const modalRef = ref(null)
const previousActiveElement = ref(null)

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  titleLevel: {
    type: String,
    default: 'h5',
    validator: (value) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', 'full'].includes(value)
  },
  customWidth: {
    type: String,
    default: ''
  },
  closable: {
    type: Boolean,
    default: true
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  closeOnEscape: {
    type: Boolean,
    default: true
  },
  zIndex: {
    type: Number,
    default: 9999
  }
})

// Emits
const emit = defineEmits(['close', 'open'])

// Computed
const modalClasses = computed(() => {
  const baseClasses = 'relative bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-xl transform transition-all'
  
  const sizeClasses = {
    sm: 'max-w-sm w-full',
    md: 'max-w-md w-full',
    lg: 'max-w-lg w-full',
    xl: 'max-w-xl w-full',
    '2xl': 'max-w-2xl w-full',
    '3xl': 'max-w-3xl w-full',
    full: 'max-w-full w-full mx-4'
  }
  
  return [
    baseClasses,
    sizeClasses[props.size]
  ].join(' ')
})

const modalStyles = computed(() => {
  if (props.customWidth) {
    return {
      maxWidth: props.customWidth,
      width: '100%'
    }
  }
  return {}
})

// Methods
const close = () => {
  emit('close')
}

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    close()
  }
}

const handleEscape = (event) => {
  if (event.key === 'Escape' && props.closeOnEscape) {
    close()
  }
}

// Focus trap implementation
const handleTabKey = (event) => {
  if (event.key !== 'Tab' || !modalRef.value) return
  
  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  
  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement?.focus()
    event.preventDefault()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement?.focus()
    event.preventDefault()
  }
}

// Focus management
const manageFocus = (shouldFocus) => {
  if (shouldFocus) {
    // Store current focus
    previousActiveElement.value = document.activeElement
    
    // Focus first focusable element in modal
    nextTick(() => {
      const focusableElements = modalRef.value?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements && focusableElements.length > 0) {
        focusableElements[0].focus()
      }
    })
  } else {
    // Return focus to previous element
    if (previousActiveElement.value && typeof previousActiveElement.value.focus === 'function') {
      previousActiveElement.value.focus()
    }
  }
}

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('keydown', handleTabKey)
    document.body.style.overflow = 'hidden'
    manageFocus(true)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.removeEventListener('keydown', handleTabKey)
  document.body.style.overflow = ''
})

// Watch for open/close changes
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('keydown', handleTabKey)
    document.body.style.overflow = 'hidden'
    manageFocus(true)
    emit('open')
  } else {
    document.removeEventListener('keydown', handleEscape)
    document.removeEventListener('keydown', handleTabKey)
    document.body.style.overflow = ''
    manageFocus(false)
  }
})
</script>